import { z } from "zod";

/**
 * Canonical analytics event taxonomy. Every `track()` call in the client uses a name from
 * `EVENT_NAMES` with the props its schema declares; the sinks (the debug ring, any registered with
 * `addTelemetrySink`) forward them as-is. A test in the client greps every `track("...")` literal against
 * this list, so a new event is added here first.
 *
 * Naming: `<area>.<thing_that_happened>`, lower snake_case, past tense where it reads naturally.
 * Prop values are flat scalars (`string | number | boolean | null`), never nested and never PII:
 * the player id travels as the analytics identity, not as a prop.
 */

const none = z.object({});
const currency = z.string().min(1);

export const EVENT_SCHEMAS = {
  // App lifecycle
  "app.booted": z.object({ ms: z.number() }),
  "app.foreground": none,
  "app.background": none,
  // Session
  "session.started": none,
  "session.ended": z.object({ duration_ms: z.number() }),
  // First-time-user funnel: one generic event, the step name is the funnel axis
  "ftue.step": z.object({ step: z.string().min(1), index: z.number().int().optional() }),
  // Progression
  "progress.milestone": z.object({ milestone: z.string().min(1), value: z.number().optional() }),
  // Economy: every source and sink of soft/hard currency, plus the reward-claim pipeline
  "economy.credit": z.object({ currency, amount: z.number(), reason: z.string().min(1) }),
  "economy.debit": z.object({ currency, amount: z.number(), reason: z.string().min(1) }),
  "economy.claim_queued": z.object({ ref: z.string(), type: z.string().optional() }),
  "economy.rejected": z.object({ ref: z.string(), reason: z.string() }),
  "economy.auth_failed": none,
  // Account (privacy rights exercised in-game)
  "account.delete": none,
  "account.export": none,
  "economy.pending": z.object({ ref: z.string(), attempt: z.number(), delay_ms: z.number() }),
  "economy.pending_expired": z.object({ ref: z.string() }),
  // Purchases (client-observed lifecycle; the grant is a server-verified entitlement)
  "purchase.started": z.object({ product_id: z.string(), kind: z.string(), platform: z.string() }),
  "purchase.completed": z.object({
    product_id: z.string(),
    kind: z.string(),
    transaction_id: z.string(),
  }),
  "purchase.failed": z.object({
    product_id: z.string().optional(),
    reason: z.string(),
    detail: z.string().optional(),
  }),
  "purchase.restored": z.object({ platform: z.string() }),
  // Release gate
  "gate.raised": z.object({ kind: z.string() }),
  // Clients follow the server's build (services/buildFollow.ts)
  "build.reload": z.object({ from: z.string(), to: z.string() }),
  "build.notice": z.object({ kind: z.string(), from: z.string(), to: z.string() }),
  // Errors
  "error.uncaught": z.object({ message: z.string(), source: z.string() }),
  "error.unhandled_rejection": z.object({ message: z.string() }),
  "error.react": z.object({ message: z.string(), stack: z.string() }),
  // Flags and data
  "flags.override": z.object({ key: z.string(), value: z.union([z.boolean(), z.string()]) }),
  "data.version_mismatch": z.object({ client: z.string(), server: z.string() }),
  // Intents and gameplay (the template playground; a real game replaces these)
  "intent.rejected": z.object({ type: z.string(), reason: z.string() }),
  "intent.stale": z.object({ type: z.string(), reason: z.string() }),
  "entity.spawned": z.object({ kind: z.string(), first: z.boolean() }),
  "entity.removed": z.object({ kind: z.string() }),
  "entity.expired": z.object({ kind: z.string() }),
  "entities.cleared": z.object({ count: z.number() }),
  // Save
  "save.conflict": z.object({
    local: z.number(),
    server: z.number(),
    ownWrite: z.boolean(),
    usable: z.boolean(),
  }),
  "save.migrated": z.object({ from: z.number() }),
  "save.gameplay_invalid": z.object({ reason: z.string() }),
  "save.reset": none,
  // Debug menu
  "debug.test": none,
  "debug.test_error": none,
} as const;

export type EventName = keyof typeof EVENT_SCHEMAS;

/** Every known event name, for tests and for sinks that allow-list. */
export const EVENT_NAMES = Object.keys(EVENT_SCHEMAS) as readonly EventName[];

/** The props a known event carries, as the caller writes them. */
export type EventProps<N extends EventName> = z.input<(typeof EVENT_SCHEMAS)[N]>;

export function isEventName(name: string): name is EventName {
  return Object.hasOwn(EVENT_SCHEMAS, name);
}
